import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, MapPin, CheckCircle, Heart, Users } from 'lucide-react';

const Home = () => {
    const schema = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "ForeverUnion",
        "url": "https://incomparable-puffpuff-1c57a9.netlify.app/",
        "potentialAction": {
            "@type": "SearchAction",
            "target": "https://incomparable-puffpuff-1c57a9.netlify.app/search?q={search_term_string}",
            "query-input": "required name=search_term_string"
        }
    };

    return (
        <div className="flex flex-col">
            <Helmet>
                <title>ForeverUnion - India's Most Trusted Matrimony Service</title>
                <meta name="description" content="Find your perfect match with ForeverUnion. India's #1 Matrimony service with millions of verified profiles. Register Free!" />
                <meta name="keywords" content="matrimony, marriage, indian matrimony, matchmaking, brides, grooms" />
                <link rel="canonical" href="https://incomparable-puffpuff-1c57a9.netlify.app/" />
                <script type="application/ld+json">
                    {JSON.stringify(schema)}
                </script>
            </Helmet>
            {/* Hero Section */}
            <div className="bg-white py-20 px-4 text-center">
                <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-6 tracking-tight">
                    Love Knows No <span className="text-pink-600">Boundaries</span>
                </h1>
                <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
                    India's most trusted matrimony brand. Join millions who found their perfect match.
                </p>

                <div className="max-w-4xl mx-auto bg-white p-4 rounded-2xl shadow-xl border border-gray-100 flex flex-col md:flex-row gap-4 items-center">
                    <div className="flex-1 w-full">
                        <label className="block text-left text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">I'm looking for</label>
                        <select className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-pink-500 transition-shadow">
                            <option>Woman</option>
                            <option>Man</option>
                        </select>
                    </div>
                    <div className="flex-1 w-full">
                        <label className="block text-left text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Age</label>
                        <div className="flex gap-2">
                            <select className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-pink-500 transition-shadow">
                                <option>20</option>
                                <option>21</option>
                                <option>22</option>
                                <option>25</option>
                            </select>
                            <span className="self-center text-gray-400">to</span>
                            <select className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-pink-500 transition-shadow">
                                <option>30</option>
                                <option>35</option>
                                <option>40</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex-1 w-full">
                        <label className="block text-left text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Religion</label>
                        <select className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-pink-500 transition-shadow">
                            <option>Any</option>
                            <option>Hindu</option>
                            <option>Muslim</option>
                            <option>Christian</option>
                            <option>Sikh</option>
                        </select>
                    </div>
                    <button className="w-full md:w-auto bg-pink-600 text-white px-8 py-4 rounded-xl font-bold hover:bg-pink-700 transition-colors shadow-lg hover:shadow-xl mt-5 md:mt-0 flex items-center justify-center gap-2">
                        <Search className="w-5 h-5" />
                        Let's Begin
                    </button>
                </div>
            </div>

            {/* Featured Cities Section */}
            <div className="bg-gray-50 py-20 px-4">
                <div className="container mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Find Matches by City</h2>
                        <p className="text-gray-600">Popular locations where people are successfully finding love</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        {/* Delhi Card */}
                        <Link to="/delhi-matrimony" className="group relative overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 h-64">
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent z-10"></div>
                            {/* Placeholder image, ideally use actual image */}
                            <div className="absolute inset-0 bg-gray-300 group-hover:scale-105 transition-transform duration-500">
                                {/* CSS Gradient placeholder for image */}
                                <div className="w-full h-full bg-gradient-to-br from-red-800 to-orange-600"></div>
                            </div>
                            <div className="absolute bottom-0 left-0 p-6 z-20 text-white">
                                <div className="flex items-center gap-2 mb-2">
                                    <MapPin className="w-5 h-5 text-pink-400" />
                                    <h3 className="text-2xl font-bold">Delhi Matrimony</h3>
                                </div>
                                <p className="text-gray-200 text-sm opacity-0 group-hover:opacity-100 transition-opacity transform translate-y-2 group-hover:translate-y-0">
                                    Find your perfect match in the heart of India.
                                </p>
                            </div>
                        </Link>

                        {/* Mumbai Card */}
                        <Link to="/mumbai-matrimony" className="group relative overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 h-64">
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent z-10"></div>
                            <div className="absolute inset-0 bg-gray-300 group-hover:scale-105 transition-transform duration-500">
                                <div className="w-full h-full bg-gradient-to-br from-blue-900 to-indigo-700"></div>
                            </div>
                            <div className="absolute bottom-0 left-0 p-6 z-20 text-white">
                                <div className="flex items-center gap-2 mb-2">
                                    <MapPin className="w-5 h-5 text-pink-400" />
                                    <h3 className="text-2xl font-bold">Mumbai Matrimony</h3>
                                </div>
                                <p className="text-gray-200 text-sm opacity-0 group-hover:opacity-100 transition-opacity transform translate-y-2 group-hover:translate-y-0">
                                    Discover love in the city of dreams.
                                </p>
                            </div>
                        </Link>
                    </div>
                </div>
            </div>

            {/* Trust Section */}
            <div className="container mx-auto py-20 px-4 text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-12">Why We Are The Best</h2>
                <div className="grid md:grid-cols-3 gap-8">
                    <div className="p-6">
                        <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-6 text-pink-600">
                            <CheckCircle className="w-8 h-8" />
                        </div>
                        <h3 className="text-xl font-bold mb-3">100% Verified Profiles</h3>
                        <p className="text-gray-600">We verify every profile to ensure a safe and secure matchmaking experience.</p>
                    </div>
                    <div className="p-6">
                        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 text-purple-600">
                            <Users className="w-8 h-8" />
                        </div>
                        <h3 className="text-xl font-bold mb-3">Best Matches</h3>
                        <p className="text-gray-600">Our proprietary algorithm finds you the most compatible matches based on your preferences.</p>
                    </div>
                    <div className="p-6">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 text-blue-600">
                            <Heart className="w-8 h-8" />
                        </div>
                        <h3 className="text-xl font-bold mb-3">Success Stories</h3>
                        <p className="text-gray-600">Join thousands of happy couples who found their soulmate on our platform.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Home;
